import { useEffect, useMemo, useState } from 'react';
import { Button, Image, Box, Flex, Link as ChakraLink, Text } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useLocation, Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ScoreBadge } from 'shared-components';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { getInterviewImages } from '../../../../api/api';
import { ApplicationState } from '../../../../store';
import { getIdentityDetails } from '../../../../store/selectors';
import { getFacialRecognitionScore } from '../../../../utilities/scheduledInterviews';
import { useSession } from 'shared-components/authentication';

export default function IdentityDetails() {
  const location = useLocation();
  const session = useSession();
  const interviewPagePath = useMemo(() => {
    const pathnameSplitted = location.pathname.split('/');
    pathnameSplitted.pop();
    return pathnameSplitted.join('/');
  }, [location.pathname]);
  const params = useParams();
  const identityDetails = useSelector((state: ApplicationState) =>
    getIdentityDetails(state, params.interviewHash ?? ''),
  );
  const [interviewImages, setInterviewImages] = useState<{ captured: string[]; passport: string }>({
    captured: [],
    passport: '',
  });
  useEffect(() => {
    (async () => {
      const idToken = await session.getIdToken();
      if (!idToken) {
        session.onLogout();
      }
      try {
        // https://enroly.atlassian.net/browse/ENROLY-844
        // when video has not yet been procesed the API returns 500
        // as the images folder does not yet exist
        const interviewImagesData = await getInterviewImages(
          idToken,
          params?.uni ?? '',
          params?.id ?? '',
          params?.intakeId ?? '',
          params?.offerId ?? '',
          identityDetails?.videoId ?? '',
        );
        if (interviewImagesData?.captured && interviewImagesData?.passport) {
          setInterviewImages(interviewImagesData);
        }
      } catch (e) {
        console.warn('processing video has not yet completed');
        console.error(e);
      }
    })();
  }, [params, identityDetails?.videoId]);
  return (
    <Flex flexDirection={'column'}>
      <ChakraLink as={Link} to={interviewPagePath} mb="3" variant="secondary" size="sm" flexShrink={0}>
        <Button leftIcon={<FontAwesomeIcon className="utility-icon" icon={regular('arrow-left')} />}>
          All questions
        </Button>
      </ChakraLink>
      <Flex bg={'chakra-subtle-bg'} pl="4" pr="2" py="3" alignItems={'center'} flex={100}>
        <Box mr={3} ml={3}>
          <ScoreBadge size={12} value={getFacialRecognitionScore(identityDetails?.score)} />
        </Box>
        <Box alignItems={'center'}>Identity score</Box>
      </Flex>
      <Flex
        bg={'chakra-subtle-bg'}
        mt={4}
        pl="4"
        pr="2"
        py="3"
        justifyContent={'center'}
        alignItems={'center'}
        flex={100}
        flexDirection="column"
      >
        {interviewImages?.passport ? (
          <>
            <Box justifyContent={'center'}>Passport Image</Box>
            <Box mt={3} p={3}>
              <Image src={interviewImages?.passport} alt={'passport'} width={'100%'} objectFit={'contain'} />
            </Box>
          </>
        ) : (
          <Text textAlign="center" color="muted" mb={0}>
            No passport image has been uploaded
          </Text>
        )}
      </Flex>
      <Flex flexDirection={'column'}>
        {interviewImages.captured.map((intImg, imgIndex) => (
          <InterviewImage key={imgIndex} src={intImg} imgNumber={imgIndex + 1} />
        ))}
      </Flex>
    </Flex>
  );
}

const InterviewImage = ({ imgNumber, src }: { imgNumber: number; src: string }) => {
  return (
    <Flex mt={3} p={3} bg={'chakra-subtle-bg'}>
      <Box display={'flex'} alignItems={'center'} flexDirection={'column'}>
        <Text mt={3} mb={3}>
          Capture Image {imgNumber}
        </Text>
        <Image src={src} alt={`intImg-${imgNumber}`} width={'100%'} objectFit={'contain'} />
      </Box>
    </Flex>
  );
};
